import { track } from '@/services/analytics'

export type ShareResult = 'shared' | 'copied' | 'cancelled'

interface ShareDetails {
  title: string
  text: string
  url: string
}

type ShareTarget = 'listing' | 'storefront'

// The share sheet doesn't say which app was picked, so every shared link reports
// utm_source=share and Mixpanel can tell shared traffic apart from direct visits.
function sharedURL(path: string, target: ShareTarget): string {
  const url = new URL(path, window.location.origin)
  url.searchParams.set('utm_source', 'share')
  url.searchParams.set('utm_medium', target)
  return url.toString()
}

function storefrontURL(username: string): string {
  return sharedURL(`/u/${encodeURIComponent(username)}`, 'storefront')
}

function listingURL(listingID: string): string {
  return sharedURL(`/listing/${encodeURIComponent(listingID)}`, 'listing')
}

async function copyToClipboard(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value)
    return
  }

  const textarea = document.createElement('textarea')
  textarea.value = value
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.opacity = '0'
  document.body.append(textarea)
  textarea.select()
  const copied = document.execCommand('copy')
  textarea.remove()

  if (!copied) throw new Error('Unable to copy link')
}

async function share(details: ShareDetails): Promise<ShareResult> {
  if (navigator.share) {
    try {
      await navigator.share(details)
      return 'shared'
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return 'cancelled'
    }
  }

  await copyToClipboard(details.url)
  return 'copied'
}

async function shareAndTrack(target: ShareTarget, details: ShareDetails): Promise<ShareResult> {
  const result = await share(details)
  if (result !== 'cancelled') track('Link Shared', { target, result })
  return result
}

export function useShare() {
  function shareStorefront(username: string): Promise<ShareResult> {
    return shareAndTrack('storefront', {
      title: `${username} on TalentSwap`,
      text: `Explore ${username}'s services on TalentSwap.`,
      url: storefrontURL(username),
    })
  }

  function shareListing(listingID: string, title: string): Promise<ShareResult> {
    return shareAndTrack('listing', {
      title: `${title} on TalentSwap`,
      text: `Check out ${title} on TalentSwap.`,
      url: listingURL(listingID),
    })
  }

  return { shareStorefront, shareListing }
}
